import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import theme from 'assets/theme'
import KNDialog from 'components/KNDialog/KNDialog'
import KNDialogFormErrors from 'components/KNDialog/KNDialogFormErrors'
import KNFormText from 'components/KNForm/KNFormText'
import KNMapLibre from 'components/KNMapLibre/KNMapLibre'
import { type MapSelection, MapSelectionMode } from 'components/KNMapLibre/types'
import { analyticsEvent } from 'global/helpers/analytics'
import { handleFormSubmission, processServerErrorMessages } from 'global/helpers/form'
import useDialog from 'global/hooks/useDialog'
import { type ReactElement, useCallback, useEffect, useState } from 'react'
import { FormProvider, type SubmitHandler, useForm, type ValidateResult } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import AreaNameDialog from './AreaNameDialog'
import { areasToMapSelections, isAreaAlreadySelected, mapSelectionToArea, minAreasValidator } from './CarrierProfile.helpers'
import { createRegion, editRegion, REGIONS_QUERY_KEY } from './CarrierProfile.service'
import { type Area, type Contact, LaneOrRegionPanel, type Region, type RegionFormValues } from './CarrierProfile.types'
import LaneOrRegionDialogActions from './LaneOrRegionDialogActions'
import LaneOrRegionDialogAreas from './LaneOrRegionDialogAreas'
import LaneOrRegionDialogContacts from './LaneOrRegionDialogContacts'
import LaneOrRegionDialogModeSwitcher from './LaneOrRegionDialogModeSwitcher'
import LaneOrRegionDialogPanel from './LaneOrRegionDialogPanel'

interface CreateOrEditRegionDialogPayload {
  region?: Region
  contacts: Contact[]
}

export interface CreateOrEditLaneDialogProps {
  payload: CreateOrEditRegionDialogPayload
  open: boolean
  onAction: () => void
  onClose: () => void
}

const CreateOrEditRegionDialog = ({
  payload,
  open,
  onAction,
  onClose,
}: CreateOrEditLaneDialogProps): ReactElement => {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const [activePanel, setActivePanel] = useState(LaneOrRegionPanel.START)
  const form = useForm<RegionFormValues>({
    defaultValues: {
      location: {
        areas: [],
      },
      contactIds: [],
    },
    criteriaMode: 'all',
  })
  const { watch, trigger, handleSubmit, reset, control, formState, getValues, setValue, setError } = form
  const createRegionMutation = useMutation({
    mutationFn: async (data: RegionFormValues) => {
      await createRegion(data)
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [REGIONS_QUERY_KEY] }),
  })
  const updateRegionMutation = useMutation({
    mutationFn: async (data: RegionFormValues) => {
      await editRegion(payload.region?.id ?? '', data)
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [REGIONS_QUERY_KEY] }),
  })
  const watchedRegionAreas = watch('location.areas')
  const [selectionMode, setSelectionMode] = useState<MapSelectionMode>()
  const [selections, setSelections] = useState<MapSelection[]>([])
  const [recentSelection, setRecentSelection] = useState<MapSelection>()
  const [autoLocationName, setAutoLocationName] = useState('')
  const [additionalInfoPanelVisible, setAdditionalInfoPanelVisible] = useState(!!payload.region)

  const handleAreaNameDialogAction = useCallback((newArea: Area) => {
    const regionAreas = getValues('location.areas')
    setValue('location.areas', [...regionAreas, newArea])
  }, [])

  const handleAreaNameDialogClose = useCallback(() => {
    setSelectionMode(undefined)
  }, [])

  const areaNameDialog = useDialog<typeof AreaNameDialog>({
    onAction: handleAreaNameDialogAction,
    onClose: handleAreaNameDialogClose,
  })

  useEffect(() => {
    if (open) {
      reset({
        location: {
          name: payload.region?.location.name,
          areas: payload.region?.location.areas ?? [],
        },
        vehiclesAmount: payload.region?.vehiclesAmount,
        contactIds: payload.region?.contacts.map((contact) => contact.id) ?? [],
      })
      setActivePanel(LaneOrRegionPanel.START)
      setAutoLocationName('')
      setAdditionalInfoPanelVisible(!!payload.region)
    }
  }, [open])

  const handlePanelChange = useCallback((panel: LaneOrRegionPanel) => {
    setActivePanel(panel)
  }, [])

  const processAutoLocationName = useCallback(() => {
    if (!getValues('location.name')) {
      setValue('location.name', autoLocationName, { shouldValidate: true })
    }
  }, [autoLocationName])

  const onSubmit: SubmitHandler<RegionFormValues> = useCallback(async (data: RegionFormValues): Promise<void> => {
    await handleFormSubmission(
      () => payload.region ? updateRegionMutation.mutateAsync(data) : createRegionMutation.mutateAsync(data),
      {
        onError: (error) => setError('root', processServerErrorMessages(error)),
        onSuccess: () => {
          if (payload.region) {
            analyticsEvent('polestar_carrier_profile_region_updated')
          } else {
            analyticsEvent('polestar_carrier_profile_region_created')
          }
          onAction()
        },
      },
    )
  }, [payload.region, onAction])

  const handleSubmitWrapper = useCallback(() => {
    processAutoLocationName()
    void handleSubmit(onSubmit)()
  }, [processAutoLocationName, onSubmit])

  useEffect(() => {
    setSelections(areasToMapSelections(watchedRegionAreas, theme.palette.secondary.main))
    setSelectionMode(undefined)
    setAutoLocationName(watchedRegionAreas.map((area) => area.name).join(', '))
  }, [watchedRegionAreas])

  const handleSelectionModeChange = useCallback((mode?: MapSelectionMode) => {
    setSelectionMode(mode)
  }, [])

  const handleSelection = useCallback((selection: MapSelection) => {
    setRecentSelection(selection)
    if (selection.mode === MapSelectionMode.CIRCLE) {
      areaNameDialog.handleOpen()
      return
    }
    const regionAreas = getValues('location.areas')
    const alreadySelectedArea = regionAreas.find((area) => isAreaAlreadySelected(area, selection))
    const newArea = mapSelectionToArea(selection)
    if (!alreadySelectedArea && newArea) {
      setValue('location.areas', [...regionAreas, newArea])
    }
    setSelectionMode(undefined)
  }, [])

  const showAdditionalInfoPanel = useCallback(async () => {
    processAutoLocationName()
    if (await trigger(['location.areas'])) {
      setAdditionalInfoPanelVisible(true)
      setActivePanel(LaneOrRegionPanel.ADDITIONAL_INFO)
    }
  }, [processAutoLocationName])

  return (
    <FormProvider {...form}>
      <KNDialog
        open={open}
        onClose={onClose}
        closeAction={false}
        maxWidth="xxl"
        preventClosing={formState.isSubmitting}
      >
        <Stack direction="row" spacing={2} height="90vh">
          <Box sx={{ margin: '-1rem 0 -1rem -1rem !important', flexGrow: 1 }}>
            <KNMapLibre
              withSelectors
              selectionSource="rcs_countries"
              selectionMode={selectionMode}
              selections={selections}
              onSelectionChange={handleSelection}
              withoutLayersControl
              customAttribution={t('screens.cs.carrier_profile.lanes_and_regions.disclaimer')}
            />
          </Box>

          <Stack direction="column" spacing={1} sx={{ width: '360px' }}>
            <Typography variant="h6" color={theme.palette.primary.main}>
              {payload.region
                ? t('screens.cs.carrier_profile.lanes_and_regions.form.edit_region')
                : t('screens.cs.carrier_profile.lanes_and_regions.form.add_new_region')}
            </Typography>
            <KNDialogFormErrors errors={formState.errors?.root} />
            <Box sx={{ flexGrow: 1 }}>
              <LaneOrRegionDialogPanel
                panelType={LaneOrRegionPanel.START}
                header={t(`screens.cs.carrier_profile.lanes_and_regions.location`)}
                activePanel={activePanel}
                onPanelChange={handlePanelChange}
              >
                <KNFormText
                  name="location.name"
                  label={t('screens.cs.carrier_profile.lanes_and_regions.form.name')}
                  placeholder={autoLocationName}
                  control={control}
                />
                <LaneOrRegionDialogAreas
                  name="location.areas"
                  label={t('screens.cs.carrier_profile.lanes_and_regions.form.areas')}
                  control={control}
                  rules={{
                    validate: {
                      minAreas: (): ValidateResult => minAreasValidator(getValues('location.areas')),
                    },
                  }}
                />
                <LaneOrRegionDialogModeSwitcher mode={selectionMode} onModeChange={handleSelectionModeChange} />
                {!additionalInfoPanelVisible && (
                  <Box paddingTop={2}>
                    <Button
                      variant="contained"
                      color="secondary"
                      size="small"
                      onClick={showAdditionalInfoPanel}
                      fullWidth
                    >
                      {t('general.next')}
                    </Button>
                  </Box>
                )}
              </LaneOrRegionDialogPanel>
              {additionalInfoPanelVisible && (
                <LaneOrRegionDialogPanel
                  panelType={LaneOrRegionPanel.ADDITIONAL_INFO}
                  header={t(`screens.cs.carrier_profile.lanes_and_regions.additional_info`)}
                  activePanel={activePanel}
                  onPanelChange={handlePanelChange}
                >
                  <KNFormText
                    name="vehiclesAmount"
                    type="number"
                    label={t('screens.cs.carrier_profile.lanes_and_regions.form.number_of_vehicles')}
                    control={control}
                    rules={{
                      required: t('form.validation.required'),
                    }}
                  />
                  <LaneOrRegionDialogContacts contacts={payload.contacts} />
                </LaneOrRegionDialogPanel>
              )}
            </Box>
            <LaneOrRegionDialogActions
              disabled={!additionalInfoPanelVisible}
              onAction={handleSubmitWrapper}
              onClose={onClose}
            />
          </Stack>
        </Stack>
        {recentSelection && (
          <AreaNameDialog
            payload={{
              selection: recentSelection,
            }}
            open={areaNameDialog.isOpen}
            onAction={areaNameDialog.handleAction}
            onClose={areaNameDialog.handleClose}
          />
        )}
      </KNDialog>
    </FormProvider>
  )
}

export default CreateOrEditRegionDialog
